import React from 'react';
import { mountForm, SystemTenantForm, unmountForm } from '@kineticdata/react';
import {
  generateFormButtons,
  FormError,
  SecretField,
  SecretFieldGroup,
  ChangeSecretField,
  Icon,
} from '@kineticdata/ui';
import { SpaceHelp } from './SpaceHelp';
import { NavListItemLink } from '../common/NavListItemLink';
import { SpaceEditUsersList } from './users/SpaceEditUsersList';
import { SpaceEditTask } from './SpaceEditTask';
import { regRoute } from '../../router';
import { navigate } from '../../root';
import { regSaga } from '../../saga';
import { call, put, takeEvery } from 'redux-saga/effects';
import { TimeAgoAndUser } from '../common/TimeAgoAndUser';
import { StatusBadge } from '../common/StatusBadge';
import { openModal } from '../common/ConfirmationModal';
import { action } from '../../store';
import { Link, useParams } from 'react-router-dom';
import { SpaceEditForm } from './SpaceEditForm';
import {
  DATABASE_ADAPTER_FIELDS,
  MssqlPropertyLayout,
  OraclePropertyLayout,
  PostgresPropertyLayout,
} from '../common/DatabaseAdapters';
import {
  fetchSystemDefaultTaskDbAdapter,
  fetchTenant,
} from '@kineticdata/react/src/apis/system';
import { spaceDeleteConfirmOptions } from './index';
import { SpaceSecurityTab } from './SpaceSecurityTab';

const tenantEditKey = spaceSlug => `${spaceSlug}-tenant-edit`;

const handleSave = () => () => {
  navigate(`/spaces`);
};

regRoute('TENANT_EDIT_PAGE', '/spaces/edit/:spaceSlug/*');

regSaga(
  takeEvery('ENTER_TENANT_EDIT_PAGE', function*({ payload }) {
    // Mount the form.
    const { spaceSlug } = payload.params;
    yield call(mountForm, tenantEditKey(spaceSlug));

    // Fetch the data needed for the db deletion checkbox.
    const { tenant } = yield call(fetchTenant, {
      slug: spaceSlug,
      include: 'details',
    });
    const { adapter } = yield call(fetchSystemDefaultTaskDbAdapter);

    let canDeleteTaskDb = false;
    let willDeleteTaskDb = false;

    if (tenant.task && tenant.task.databaseAdapter) {
      const tenantHost = tenant.task.databaseAdapter.properties.find(
        p => p.name === 'host',
      );
      const tenantPort = tenant.task.databaseAdapter.properties.find(
        p => p.name === 'port',
      );

      canDeleteTaskDb = tenant.task.databaseAdapter.type === 'postgres';

      if (!!adapter) {
        const defaultHost = adapter.properties.find(p => p.name === 'host');
        const defaultPort = adapter.properties.find(p => p.name === 'port');
        // WillDeleteTaskDb is used to control the UI for a checkbox
        willDeleteTaskDb =
          tenantHost.value === defaultHost.value &&
          tenantPort.value === defaultPort.value;
      }
    }

    yield put({
      type: 'SET_DELETE_TASK_DB',
      payload: {
        canDeleteTaskDb,
        willDeleteTaskDb,
      },
    });
  }),
);

regSaga(
  takeEvery('LEAVE_TENANT_EDIT_PAGE', function*({ payload }) {
    const { spaceSlug } = payload.params;
    yield call(unmountForm, tenantEditKey(spaceSlug));
    yield put({ type: 'RESET_DELETE_TASK_DB' });
  }),
);

const FormLayout = ({ fields, error, buttons, bindings }) => {
  const currentAdapter = bindings.values.get('task_databaseAdapter_type');

  return (
    <form>
      <div className="row">
        <div className="col-md-3">{fields.get('replicas')}</div>
        <div className="col-md-9">{fields.get('image')}</div>
      </div>
      <SecretFieldGroup
        secretField={fields.get('authenticationSecret')}
        changeSecretField={fields.get('changeAuthenticationSecret')}
      />
      <div className="heading heading-sm">
        <h2 className="h3">Task Database</h2>
      </div>
      {fields.get('task_databaseAdapter_type')}
      {currentAdapter === 'mssql' ? (
        <MssqlPropertyLayout fields={fields} values={bindings.values} />
      ) : currentAdapter === 'oracle' ? (
        <OraclePropertyLayout fields={fields} values={bindings.values} />
      ) : currentAdapter === 'postgres' ? (
        <PostgresPropertyLayout fields={fields} values={bindings.values} />
      ) : null}
      {error}
      <div className="form-buttons">{buttons}</div>
    </form>
  );
};

export const spaceConsoleUrl = spaceSlug => {
  const { protocol, host } = window.location;
  return `${protocol}//${spaceSlug}.${host}/app/`;
};

export const SpaceEdit = () => {
  const { spaceSlug, tab } = useParams();

  const handleDelete = () =>
    openModal({
      ok: action('DELETE_SPACE', spaceSlug),
      ...spaceDeleteConfirmOptions({ spaceSlug }),
    });

  return (
    <SystemTenantForm
      formKey={tenantEditKey(spaceSlug)}
      onSave={handleSave}
      slug={spaceSlug}
      components={{
        FormError,
        FormButtons: generateFormButtons({
          handleDelete,
          submitLabel: 'Update Space',
          cancelPath: '/spaces',
        }),
        FormLayout,
      }}
      alterFields={{
        changeAuthenticationSecret: { component: ChangeSecretField },
        authenticationSecret: { component: SecretField },
        ...DATABASE_ADAPTER_FIELDS(spaceSlug),
      }}
    >
      {({ form, initialized, bindings: { tenant: spaceBindings } }) =>
        initialized && (
          <div className="app-content__main">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/spaces">Spaces</Link>
                </li>
                <li className="breadcrumb-item active">
                  {spaceBindings.getIn(['space', 'name'])}
                </li>
              </ol>
            </nav>
            <div className="heading">
              <h1>
                {spaceBindings.getIn(['space', 'name'])}
                <SpaceHelp />
              </h1>
              <div className="btn-set">
                <a
                  className="btn btn-primary"
                  href={spaceConsoleUrl(spaceSlug)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span>Space Console</span>
                  <Icon name="arrow-up-right" />
                </a>
              </div>
            </div>
            <dl className="metadata">
              <div>
                <dt>Created:</dt>
                <dd>
                  <TimeAgoAndUser
                    time={spaceBindings.getIn(['space', 'createdAt'])}
                    username={spaceBindings.getIn(['space', 'createdBy'])}
                  />
                </dd>
              </div>
              <div>
                <dt>Updated:</dt>
                <dd>
                  <TimeAgoAndUser
                    time={spaceBindings.getIn(['space', 'updatedAt'])}
                    username={spaceBindings.getIn(['space', 'updatedBy'])}
                  />
                </dd>
              </div>
              <div>
                <dt>Status:</dt>
                <dd>
                  <StatusBadge
                    sm
                    value={spaceBindings.getIn(['space', 'status'])}
                  />
                </dd>
              </div>
              {spaceBindings.getIn(['space', 'statusMessage']) && (
                <div>
                  <dt>Message:</dt>
                  <dd>{spaceBindings.getIn(['space', 'statusMessage'])}</dd>
                </div>
              )}
            </dl>

            <div className="columns">
              <div className="col-nav">
                <ul className="nav flex-column">
                  <NavListItemLink to={`/spaces/edit/${spaceSlug}/general`}>
                    General
                  </NavListItemLink>
                  <NavListItemLink to={`/spaces/edit/${spaceSlug}/users`}>
                    Users
                  </NavListItemLink>
                  <NavListItemLink to={`/spaces/edit/${spaceSlug}/infra`}>
                    Infrastructure
                  </NavListItemLink>
                  <NavListItemLink to={`/spaces/edit/${spaceSlug}/security`}>
                    Security
                  </NavListItemLink>
                  <NavListItemLink to={`/spaces/edit/${spaceSlug}/task`}>
                    Task Engine
                  </NavListItemLink>
                </ul>
              </div>
              <div className="col">
                {tab === 'general' ? (
                  <SpaceEditForm slug={spaceSlug} onDelete={handleDelete} />
                ) : tab === 'users' ? (
                  <SpaceEditUsersList spaceSlug={spaceSlug} />
                ) : tab === 'infra' ? (
                  <div className="content-container">{form}</div>
                ) : tab === 'security' ? (
                  <SpaceSecurityTab spaceSlug={spaceSlug} />
                ) : tab === 'task' ? (
                  <SpaceEditTask spaceSlug={spaceSlug} />
                ) : null}
              </div>
            </div>
          </div>
        )
      }
    </SystemTenantForm>
  );
};
